import React from 'react';

import debounce from 'lodash/debounce';

import { makeStyles, Stack } from '@gogame/components';
import { useMountedState } from '@gogame/hooks-ts';
import { useParams } from '@gogame/screens';
import { useScreenState, withScreen, Screen } from '@gogame/screens';
import { useSocket } from '@gogame/sockets';

import { useLoader } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Loader';
import { PlanSettingsContext } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/PlanSettings';
import {
  PlanStateContext,
  usePlanState,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/PlanState';
import { MarketingPlanningSocketsProvider } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Socket';
import { EVENT_RELOAD_NODES } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Socket/constants';
import {
  useSocketReloadMeta,
  useSocketUpdateMetricTypes,
  useSocketUpdateActivityTypes,
  useSocketUpdateUnsavedChanges,
  useSocketCreateNodes,
  useSocketUpdateNodes,
  useSocketDeleteNodes,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Socket/hooks';
import {
  ValidationContext,
  useValidation,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Validation';
import {
  ViewStateContext,
  useViewState,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/ViewState';
import {
  RE,
  UA,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';
import {
  useLoadMarketingPlan,
  useLoadMetaMarketingPlan,
  useSaveActivityTypesMarketingPlan,
  useSaveMetricTypesMarketingPlan,
  useSaveNodesMarketingPlan,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/hooks';
import { loadAllMetricTypes } from 'scenes/MarketingPlanning/MarketingPlanningWizard/sources/plan';

import Levels from './Levels';
import TreeForm from './TreeForm';
import { WizardViewStyled } from './components';
import MarketingPlanningActions from './components/MarketingPlanningActions';

const useStyles = makeStyles(() => {
  return {
    content: {
      height: '100%',
      // minHeight: '700px',
    },
  };
});
const MarketingPlanningForm = withScreen({
  name: 'marketing-planning-form',
})(() => {
  const classes = useStyles();
  const { year, month } = useParams();
  const { version } = useScreenState();
  const { loading, processAsync } = useLoader();
  const [meta, setMeta] = useMountedState(null);
  const [metricTypes, setMetricTypes] = useMountedState(null);
  const [data, setData] = useMountedState(null);
  const loadMetaMarketingPlan = useLoadMetaMarketingPlan(processAsync);
  const loadMarketingPlan = useLoadMarketingPlan(processAsync);
  const saveMetricTypesMarketingPlan =
    useSaveMetricTypesMarketingPlan(processAsync);
  const saveActivityTypesMarketingPlan =
    useSaveActivityTypesMarketingPlan(processAsync);
  const saveNodes = useSaveNodesMarketingPlan();
  const saveNodesDebounce = React.useCallback(debounce(saveNodes, 1000), []);
  const socket = useSocket();

  const [
    state,
    {
      onChangeField,
      onAddNode,
      onRemoveNode,
      onUpdateNode,
      onResetBudget,
      onApplyMetricBellow,
    },
  ] = usePlanState({
    initialValues: data,
    onSaveState: saveNodesDebounce,
  });

  const viewState = useViewState({
    initialValues: state,
    metricTypes,
    meta,
  });

  const validationErrors = useValidation({
    initialValues: viewState,
  });

  const loadMeta = React.useCallback(() => {
    return loadMetaMarketingPlan({
      year: year,
      month: month,
      version: version,
    }).then(setMeta);
  }, [year, month, version]);

  const loadData = React.useCallback(() => {
    return loadMarketingPlan({
      year: year,
      month: month,
    }).then(setData);
  }, [year, month]);

  React.useEffect(() => {
    loadAllMetricTypes().then(setMetricTypes);
  }, []);

  React.useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const changeMetrics = React.useCallback((value) => {
    setMeta((state) => ({
      ...state,
      metricTypes: value,
    }));
  }, []);

  const switchReBudget = React.useCallback((value) => {
    setMeta((state) => ({
      ...state,
      activityTypes: value,
    }));
  }, []);

  React.useEffect(() => {
    socket.on(EVENT_RELOAD_NODES, loadData);
    return () => {
      socket.off(EVENT_RELOAD_NODES, loadData);
    };
  }, [socket, loadData]);

  useSocketReloadMeta(loadMeta, [loadMeta]);

  useSocketUpdateMetricTypes(
    ({ data }) => {
      changeMetrics(data);
    },
    meta,
    [changeMetrics]
  );

  useSocketUpdateActivityTypes(
    ({ data }) => {
      switchReBudget(data);
    },
    meta,
    [switchReBudget]
  );

  useSocketUpdateUnsavedChanges(() => {
    setMeta({ ...meta, hasUnsavedChanges: true });
  }, [setMeta, meta]);

  useSocketCreateNodes(onAddNode, meta, [onAddNode]);

  useSocketDeleteNodes(onRemoveNode, meta, [onRemoveNode]);

  useSocketUpdateNodes(onUpdateNode, meta, [onUpdateNode]);

  const handlerChangeMetrics = React.useCallback(
    (value) => {
      changeMetrics(value);

      saveMetricTypesMarketingPlan({
        year: year,
        month: month,
        data: value,
      });
    },
    [year, month, changeMetrics, saveMetricTypesMarketingPlan]
  );

  const handlerSwitchReBudget = React.useCallback(
    (value) => {
      const newActivityTypes = value ? [UA, RE] : [UA];

      switchReBudget(newActivityTypes);

      saveActivityTypesMarketingPlan({
        year: year,
        month: month,
        data: newActivityTypes,
      });
    },
    [year, month, switchReBudget, saveActivityTypesMarketingPlan]
  );

  return (
    <React.Fragment>
      <Screen.Toolbar />
      <Screen.Body>
        <WizardViewStyled loading={loading}>
          {viewState ? (
            <PlanSettingsContext.Provider
              value={{
                meta,
                metricTypes,
                api: {
                  onChangeMetrics: handlerChangeMetrics,
                  onSwitchReBudget: handlerSwitchReBudget,
                },
              }}
            >
              <PlanStateContext.Provider
                value={{
                  state,
                  api: { onResetBudget, onApplyMetricBellow, onChangeField },
                }}
              >
                <MarketingPlanningSocketsProvider>
                  <ViewStateContext.Provider value={viewState}>
                    <MarketingPlanningActions
                      hasUnsavedChanges={meta.hasUnsavedChanges}
                    />
                    <ValidationContext.Provider value={validationErrors}>
                      <Stack
                        direction={'row'}
                        alignItems={'start'}
                        className={classes.content}
                        data-cy={'MarketingPlanningForm'}
                      >
                        <TreeForm />
                        <Levels />
                      </Stack>
                    </ValidationContext.Provider>
                  </ViewStateContext.Provider>
                </MarketingPlanningSocketsProvider>
              </PlanStateContext.Provider>
            </PlanSettingsContext.Provider>
          ) : null}
        </WizardViewStyled>
      </Screen.Body>
    </React.Fragment>
  );
});

export default MarketingPlanningForm;
